import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * FR-92 — the one piece of architecture the feature actually needed.
 *
 * `ScenarioPlanningChart` held its selected scenario in component-local
 * `useState`, so no other surface could observe it. That is why "as I make
 * changes in the simulation … the products we're recommending should
 * correspond" was structurally impossible: there was no channel between the
 * simulation and anything else.
 *
 * This lifts exactly one value — the selected scenario id — to app scope, and
 * mirrors it to sessionStorage so it survives a reload and a full-page
 * navigation between /dashboard and /messages.
 *
 * Deliberately minimal. It does NOT hold driver values, results, or anything
 * else; widening it is a decision for whoever needs the next field.
 */

const STORAGE_KEY = 'simulation_selected_scenario';

interface SimulationState {
  /** Scenario id from ScenarioPlanningChart, or null when none is selected. */
  selectedScenarioId: string | null;
  setSelectedScenarioId: (id: string | null) => void;
}

const SimulationStateContext = createContext<SimulationState | undefined>(undefined);

function readInitial(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function SimulationStateProvider({ children }: { children: React.ReactNode }) {
  const [selectedScenarioId, setState] = useState<string | null>(readInitial);

  const setSelectedScenarioId = useCallback((id: string | null) => {
    setState(id);
    try {
      if (id) sessionStorage.setItem(STORAGE_KEY, id);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — in-memory state still works for this session */
    }
  }, []);

  const value = useMemo(
    () => ({ selectedScenarioId, setSelectedScenarioId }),
    [selectedScenarioId, setSelectedScenarioId]
  );

  return <SimulationStateContext.Provider value={value}>{children}</SimulationStateContext.Provider>;
}

/**
 * Safe outside the provider: returns a null scenario and a no-op setter rather
 * than throwing, so a surface that has not been wrapped degrades to today's
 * behaviour instead of crashing.
 */
export function useSimulationState(): SimulationState {
  const ctx = useContext(SimulationStateContext);
  if (ctx) return ctx;
  return { selectedScenarioId: null, setSelectedScenarioId: () => undefined };
}
